<script setup>
import { ref, watch, computed } from "vue";
import { storeToRefs } from "pinia";
import { useUserStore } from "@/stores/useUserStore";
import { usePlanStore } from "@/stores/usePlanStore";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";
import { useRouter, useRoute } from "vue-router";

import SubscriptionForm from "./SubscriptionMPForm.vue";
import BillingProfileForm from "./BillingProfileForm.vue";
import SubscriptionResult from "./SubscriptionResult.vue";
import PlanCatalogByName from "./PlanCatalogByName.vue";
import DowngradeFree from "./DowngradeFree.vue";
import CouponModal from "./CouponModal.vue";

// ── PATCH: LegalDocsModal para links legais no checkout ──────────────────
import LegalDocsModal from "@/components/Legal/LegalDocsModal.vue";
// ── FIM PATCH ─────────────────────────────────────────────────────────────

import { useOnboardingStore } from "@/stores/useOnboardingStore";

const props = defineProps({
  initialStep: { type: Number, default: 2 },
  usageTypeFilter: { type: String, default: null },
  showFreeContinueBtn: { type: Boolean, default: false },

  // ✅ PATCH:
  // Quando UpgradePage é usado como modal dentro do Dashboard/AccountSummary,
  // fechar o catálogo NÃO deve navegar nem fechar o AccountSummary.
  // No uso como página real de onboarding, pode continuar redirecionando.
  redirectOnCatalogClose: { type: Boolean, default: true },

  // z-index do overlay: o padrão 1100 cobre o AccountSettingsModal (z 1000).
  // Contextos com modais mais altos (formulários de nota, z ~10000) devem
  // passar um valor maior para o catálogo abrir por cima deles.
  zIndex: { type: Number, default: 1100 },
});

// ✅ ALTERADO:
// - "close" permanece para fluxos finais, como concluir pagamento/onboarding.
// - "close-upgrade-modal" fecha apenas o modal de troca de plano,
//   sem sinalizar fechamento do AccountSummary/Profile.
const emit = defineEmits([
  "close",
  "close-upgrade-modal",
  "managed-accounts-needed",
]);

// ── PATCH: estado do LegalDocsModal ──────────────────────────────────────
const showLegalModal = ref(false);
const legalModalDefaultDoc = ref("terms");

function openLegalModal(docId) {
  legalModalDefaultDoc.value = docId;
  showLegalModal.value = true;
}
// ── FIM PATCH ─────────────────────────────────────────────────────────────

const selectedPlan = ref(null);
const selectedMode = ref(null);
const currentStep = ref(props.initialStep || 2);

const subscriptionResult = ref(null);
const isInitializing = ref(true);
const freePlanCheckCompleted = ref(false);
const isProcessing = ref(false);
const isCardProcessing = ref(false);

// ✅ ESTADOS DE CUPOM
const couponCode = ref("");
const isCouponModalOpen = ref(false);
const appliedCouponData = ref(null);
const couponApplyToAllCycles = ref(false);

const userStore = useUserStore();
const planStore = usePlanStore();
const subscriptionStore = useSubscriptionStore();
const { activeSubscription } = storeToRefs(subscriptionStore);
const onboardingStore = useOnboardingStore();

const router = useRouter();
const route = useRoute();

const BRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const cpf = ref("");
const requiresCPF = computed(() => selectedMode.value === "pix_onetime");
const isPixDisabled = computed(
  () => requiresCPF.value && cpf.value.trim() === ""
);

const showResultModal = ref(false);

function openResultModal() {
  showResultModal.value = true;
}

function closeResultModal() {
  showResultModal.value = false;
}

const modalPanelClass = computed(() => {
  const base = "relative w-full max-w-[1344px] h-[94vh] self-start";
  if (currentStep.value === 2) {
    return `${base} bg-[var(--bg-main)] rounded-2xl shadow-none p-0 overflow-y-auto`;
  }
  return `${base} bg-surface rounded-2xl shadow-2xl p-6 sm:p-8 overflow-y-auto`;
});

watch(
  () => props.initialStep,
  (val) => {
    if (Number.isInteger(val) && val >= 2) {
      currentStep.value = val;
    }
  }
);

watch(
  () => userStore.currentUser,
  async (newUser) => {
    if (newUser && newUser.userId && !freePlanCheckCompleted.value) {
      freePlanCheckCompleted.value = true;
      isInitializing.value = true;
      try {
        await subscriptionStore.fetchSubscriptionsPaged(1, 100, newUser.userId);
        if (!activeSubscription.value) {
          await planStore.fetchActivePlans();
          const freePlan = planStore.plans.find(
            (p) => p.name.toLowerCase() === "free"
          );
          if (freePlan) {
            await subscriptionStore.createSubscription({
              planId: freePlan.planId,
              paymentMethod: "free",
              transactionId: "N/A",
              trialDays: freePlan.trialDays || 0,
            });
          }
        }
      } catch (error) {
        console.error("Erro Free inicial:", error);
      } finally {
        isInitializing.value = false;
      }
    }
  },
  { immediate: true }
);

watch(
  () => route.query,
  (query) => {
    const qStep = Number(query.step);
    if (Number.isInteger(qStep) && qStep >= 2) {
      currentStep.value = qStep;
    }
  },
  { immediate: true }
);

function handlePlanSelected({ plan, mode }) {
  selectedPlan.value = plan;
  selectedMode.value = mode;
  appliedCouponData.value = null;
  couponCode.value = "";
  couponApplyToAllCycles.value = false;

  if (mode === "downgrade_free") {
    currentStep.value = 6;
  } else {
    currentStep.value = 7;
  }
}

function handleCouponApplied(data) {
  appliedCouponData.value = {
    discountValue: data.discountValue,
    finalPrice: data.finalPrice,
    originalPrice: selectedPlan.value.price,
    code: data.code,
    applyToAllCycles: data.applyToAllCycles,
  };
  couponCode.value = data.code;
  couponApplyToAllCycles.value = data.applyToAllCycles || false;
}

const summaryInfo = computed(() => {
  const p = selectedPlan.value;
  if (!p) return null;
  const billing = (p.billingCycle || "").toLowerCase();
  const price = BRL.format(Number(p.price || 0));
  let cycleLabel =
    billing === "monthly"
      ? "Mensal"
      : billing === "yearly"
      ? "Anual"
      : "Pagamento único";
  let modeLabel =
    selectedMode.value === "pix_onetime"
      ? "PIX (Pagamento único)"
      : selectedMode.value === "card_onetime"
      ? "Cartão (Pagamento único)"
      : selectedMode.value === "card_recurring"
      ? billing === "yearly"
        ? "Cartão (Assinatura anual)"
        : "Cartão (Assinatura mensal)"
      : "Plano Gratuito";

  return {
    planName: p.name,
    description: p.description,
    price,
    cycleLabel,
    modeLabel,
  };
});

async function handleSubscriptionResult(result) {
  console.log(
    "[UpgradePage] result:",
    result,
    "usageType:",
    onboardingStore.usageType
  );
  subscriptionResult.value = result;
  isProcessing.value = true;
  try {
    const { success, status } = result;
    const normalizedStatus = (status || "").toLowerCase();
    const isApprovedLike =
      !status ||
      normalizedStatus === "authorized" ||
      normalizedStatus === "approved" ||
      normalizedStatus === "active" ||
      normalizedStatus === "free_upgrade";

    if (success && isApprovedLike) {
      await subscriptionStore.fetchSubscriptionsPaged(
        1,
        100,
        userStore.currentUser?.userId
      );
      // O evento de conversão (Google/Meta) NÃO é mais disparado aqui. O backend
      // marca paymentConversionPending=true na aprovação; o refreshCurrentUser
      // abaixo consome essa flag em useUserStore._handlePaymentConversionPending,
      // que é o ponto único de disparo. Garante uma contagem por conversão,
      // inclusive quando a confirmação chega fora desta sessão (PIX tardio).
      await userStore.refreshCurrentUser();
      if (onboardingStore.requiresPayment) {
        if (onboardingStore.isEdu) {
          router.push({ name: "SchoolSetupWizard" });
        } else {
          console.log("[UpgradePage] emitindo managed-accounts-needed");
          emit("managed-accounts-needed");
        }
      } else {
        openResultModal();
      }
    } else if (normalizedStatus === "pending") {
      openResultModal();
    } else if (result.isFreePlanUpdate) {
      await userStore.refreshCurrentUser();
      currentStep.value = 5;
    } else {
      openResultModal();
    }
  } catch (e) {
    console.error("Erro ao processar resultado:", e);
    subscriptionResult.value = {
      success: false,
      message: "Erro ao processar resultado.",
    };
    openResultModal();
  } finally {
    isProcessing.value = false;
  }
}

function retryPayment() {
  closeResultModal();
  subscriptionResult.value = null;
  currentStep.value = 3;
}

function selectDifferentPlan() {
  closeResultModal();
  selectedPlan.value = null;
  selectedMode.value = null;
  subscriptionResult.value = null;
  appliedCouponData.value = null;
  couponCode.value = "";
  couponApplyToAllCycles.value = false;
  currentStep.value = 2;
}

// ✅ NOVO: ressincroniza as stores globais que alimentam o chip de plano do
// DashboardLayout. É a MESMA sequência que o AccountSummary roda ao montar e
// que comprovadamente faz o chip do header recalcular (via o watcher de
// identidade já existente no DashboardLayout). Centralizar aqui garante que,
// ao concluir o fluxo de upgrade — avulso (PIX/cartão) ou recorrente — o
// header reflita o plano recém-gravado no banco, sem depender de polling nem
// de o AccountSummary estar montado.
async function resyncGlobalSubscriptionState() {
  try {
    await userStore.refreshCurrentUser();
    await userStore.fetchCurrentUserSubscription();
    if (userStore.currentUser?.userId) {
      await subscriptionStore.fetchSubscriptionsPaged(
        1,
        100,
        userStore.currentUser.userId
      );
    }
  } catch (e) {
    console.error("[UpgradePage] Falha ao ressincronizar stores ao concluir:", e);
  }
}

async function finishOnboarding() {
  closeResultModal();

  // ✅ NOVO: garante que userStore/subscriptionStore reflitam o plano já
  // gravado no banco ANTES de fechar o modal. Assim o chip do header
  // (DashboardLayout) recalcula na hora, em vez de só quando o AccountSummary
  // for montado. Cobre avulso e recorrente igualmente.
  await resyncGlobalSubscriptionState();
  router.push({ name: "DashboardHome" });
  emit("close");
}

// ✅ PATCH: Garantia explícita de que fechamento do catálogo NÃO propaga "close" para AccountSettingsModal/AccountSummary
function handleClosePlanCatalog() {
  // ✅ NOVO COMENTÁRIO: Sempre priorizar close-upgrade-modal quando estamos no contexto de modal
  // (redirectOnCatalogClose === false). Isso previne que o emit "close" do UpgradePage
  // feche o AccountSettingsModal que contém o AccountSummary.
  if (!props.redirectOnCatalogClose) {
    emit("close-upgrade-modal");
    return;
  }

  // Comportamento original preservado para uso como página full (onboarding)
  if (props.redirectOnCatalogClose && route.name === "UpgradePage") {
    router.push({ name: "DashboardHome" });
    return;
  }

  // Fallback seguro
  emit("close-upgrade-modal");
}

function handleContinueFree() {
  onboardingStore.reset();
  emit("close");
}
</script>

<template>
  <Teleport to="body">
    <!-- ✅ PATCH:
   O AccountSettingsModal usa z-index: 1000.
   Portanto, o modal de upgrade precisa ficar acima dele.
   Antes usava z-40, por isso o catálogo abria por baixo do AccountSummary. -->
    <div
      class="upgrade-page-overlay fixed inset-0 bg-black/20 flex justify-center items-start p-2 sm:p-3 overflow-y-auto"
      :style="{ zIndex: props.zIndex }"
      @click.stop
    >
      <div :class="modalPanelClass">
        <!-- Overlay de processamento -->
        <div
          v-if="isProcessing"
          class="absolute inset-0 bg-surface/75 flex items-center justify-center z-50"
        >
          <div
            class="bg-surface rounded-lg p-6 text-center shadow-lg border border-gray-200"
          >
            <div
              class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"
            ></div>
            <p class="text-lg text-gray-700">Processando...</p>
          </div>
        </div>

        <!-- STEP 2: Catálogo de planos -->
        <PlanCatalogByName
          v-if="currentStep === 2"
          class="h-full"
          @select="handlePlanSelected"
          @close="handleClosePlanCatalog"
          :usage-type-filter="props.usageTypeFilter"
          :show-free-continue-btn="props.showFreeContinueBtn"
          @continue-free="handleContinueFree"
        />

        <!-- STEP 7: Formulário fiscal -->
        <div v-if="currentStep === 7">
          <BillingProfileForm
            @confirmed="currentStep = 3"
            @back="currentStep = 2"
          />
        </div>

        <!-- STEP 3: Checkout -->
        <div v-if="currentStep === 3">
          <!-- ← Link voltar, canto superior esquerdo -->
          <button
            v-if="!isCardProcessing"
            type="button"
            @click="currentStep = 2"
            class="voltar-link"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="voltar-link__icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar aos planos
          </button>

          <div
            class="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-8 items-start"
          >
            <div class="space-y-6">
              <div>
                <h2 class="text-3xl font-semibold text-[color:var(--text-dark)]">
                  Conclua sua Assinatura
                </h2>
                <p class="mt-2 text-base text-[color:var(--text-light)]">
                  Você está assinando o plano
                  <span class="font-semibold text-black">{{
                    selectedPlan?.name
                  }}</span
                  >.
                </p>
              </div>

              <div v-if="requiresCPF" class="space-y-2">
                <label for="cpf" class="text-sm font-medium text-slate-700"
                  >CPF do Pagador</label
                >
                <input
                  id="cpf"
                  v-model="cpf"
                  type="text"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--text-important)] focus:border-[color:var(--text-important)]"
                  placeholder="000.000.000-00"
                  required
                />
                <p v-if="isPixDisabled" class="text-sm text-red-600">
                  O CPF é obrigatório para pagamento via PIX.
                </p>
              </div>

              <div class="space-y-2">
                <label class="text-sm font-medium text-slate-700"
                  >Possui um cupom?</label
                >
                <div v-if="!appliedCouponData">
                  <button
                    @click="isCouponModalOpen = true"
                    type="button"
                    class="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 6v6m0 0v6m0-6h6m-6h-6"
                      />
                    </svg>
                    Adicionar cupom de desconto
                  </button>
                </div>
                <div
                  v-else
                  class="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2"
                >
                  <span class="text-sm text-green-700 font-medium italic">
                    Cupom
                    <strong>{{ appliedCouponData.code }}</strong> aplicado!
                  </span>
                  <button
                    @click="
                      appliedCouponData = null;
                      couponCode = '';
                      couponApplyToAllCycles = false;
                    "
                    class="text-red-500 text-xs hover:underline"
                  >
                    Remover
                  </button>
                </div>
              </div>

              <!-- Resumo da assinatura -->
              <div
                v-if="summaryInfo"
                class="bg-[var(--bg-main)] border border-[#f3e2cf] rounded-xl p-5"
              >
                <h3 class="text-lg font-semibold text-[color:var(--text-dark)] mb-3">
                  Resumo da assinatura
                </h3>
                <p class="text-sm text-[color:var(--text-light)]">
                  <span class="font-semibold">{{ summaryInfo.planName }}</span>
                  · {{ summaryInfo.cycleLabel }}
                </p>

                <div
                  v-if="appliedCouponData"
                  class="mt-2 space-y-1 border-t border-orange-100 pt-2"
                >
                  <p class="text-sm text-gray-500 line-through">
                    De: {{ BRL.format(appliedCouponData.originalPrice) }}
                  </p>

                  <p class="text-sm text-green-600 font-medium">
                    <template v-if="selectedMode === 'card_recurring'">
                      {{
                        couponApplyToAllCycles
                          ? "Desconto aplicado em todos os meses"
                          : "Desconto aplicado apenas no 1º mês"
                      }}
                    </template>
                    <template v-else> Desconto aplicado </template>
                  </p>

                  <p class="text-2xl font-bold text-[color:var(--text-dark)]">
                    {{ BRL.format(appliedCouponData.finalPrice) }}
                  </p>
                </div>
                <p v-else class="mt-2 text-2xl font-bold text-[color:var(--text-dark)]">
                  {{ summaryInfo.price }}
                </p>

                <p class="mt-1 text-xs text-[color:var(--text-light)]">
                  Método: {{ summaryInfo.modeLabel }}
                </p>
              </div>

              <!-- ── PATCH: links legais abrem LegalDocsModal internamente ── -->
              <p v-if="summaryInfo" class="legal-links">
                Sua privacidade é importante para nós. Suas informações serão
                utilizadas conforme descritos nos nossos
                <button
                  type="button"
                  class="legal-link-btn"
                  @click="openLegalModal('terms')"
                >
                  Termos de Uso
                </button>
                e na nossa
                <button
                  type="button"
                  class="legal-link-btn"
                  @click="openLegalModal('privacy')"
                >
                  Política de Privacidade</button
                >.
              </p>

              <Teleport to="body">
                <LegalDocsModal
                  v-if="showLegalModal"
                  :defaultDoc="legalModalDefaultDoc"
                  @close="showLegalModal = false"
                />
              </Teleport>
              <!-- ── FIM PATCH ────────────────────────────────────────────── -->
            </div>

            <div class="w-full max-w-xl mx-auto lg:mx-0">
              <SubscriptionForm
                :selected-plan="selectedPlan"
                :mode="selectedMode"
                :cpf="cpf"
                :coupon-code="couponCode"
                :final-price="appliedCouponData?.finalPrice || null"
                :coupon-apply-to-all-cycles="couponApplyToAllCycles"
                :is-pix-disabled="isPixDisabled"
                @subscription-result="handleSubscriptionResult"
                @card-loading-change="isCardProcessing = $event"
              />
            </div>
          </div>
        </div>

        <!-- STEP 5: Sucesso downgrade/free -->
        <div
          v-if="currentStep === 5"
          class="w-full max-w-lg mx-auto text-center p-6 flex flex-col items-center justify-center"
        >
          <div
            class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100"
          >
            <svg
              class="h-8 w-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <p class="mt-5 text-lg text-[color:var(--text-light)]">
            <span class="font-semibold text-green-700 whitespace-pre-line">{{
              subscriptionResult?.message
            }}</span>
          </p>
          <button
            class="mt-10 w-full rounded-lg px-6 py-4 text-lg font-semibold bg-[var(--bg-sidebar-left)] text-[color:var(--text-dark)]"
            @click="finishOnboarding"
          >
            Continuar
          </button>
        </div>

        <!-- STEP 6: Downgrade para Free -->
        <div v-if="currentStep === 6" class="w-full max-w-lg mx-auto">
          <DowngradeFree
            :selected-plan="selectedPlan"
            @back="currentStep = 2"
            @subscription-result="handleSubscriptionResult"
          />
        </div>
      </div>

      <!-- Modal de resultado -->
      <div
        v-if="showResultModal"
        class="result-modal-overlay"
        role="dialog"
        aria-modal="true"
      >
        <div class="result-modal-panel" @click.stop>
          <SubscriptionResult
            :result="subscriptionResult"
            @retry="retryPayment"
            @change-plan="selectDifferentPlan"
            @complete="finishOnboarding"
          />
        </div>
      </div>

      <Teleport to="body">
        <CouponModal
          v-if="isCouponModalOpen"
          :plan-id="selectedPlan?.planId"
          @close="isCouponModalOpen = false"
          @applied="handleCouponApplied"
        />
      </Teleport>
    </div>
  </Teleport>
</template>

<style scoped>
/* ✅ PATCH:
   Mantém o UpgradePage acima do AccountSettingsModal,
   cujo overlay usa z-index: 1000. */
.upgrade-page-overlay {
  z-index: 1100;
}

.result-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(10, 14, 26, 0.2);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 14px;
}

.result-modal-panel {
  width: min(520px, 96vw);
  margin-top: 56px;
  background: var(--surface);
  border: 1px solid #e6e6e6;
  border-radius: 18px;
  box-shadow: 0 26px 70px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.voltar-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 1.25rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
  padding: 0;
  transition: color 0.15s;
}

.voltar-link:hover {
  color: var(--text-dark);
}

.voltar-link__icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

/* ✅ Links legais discretos */
.legal-links {
  font-size: 0.75rem;
  color: #9aa0a6;
  line-height: 1.6;
  margin-top: 0.25rem;
}

/* ── PATCH: botão-link nos termos legais do checkout ── */
.legal-link-btn {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font-size: inherit;
  font-family: inherit;
  font-weight: bold;
  color: #9aa0a6;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.15s;
}

.legal-link-btn:hover {
  color: var(--text-light);
}
/* ── FIM PATCH ─────────────────────────────────────────── */
</style>